import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MaterialsData } from "../../../../ThermalDesignTypes";
import ModalDropdown from "../../../../../../../CommonComponents/ModalDropdown";
import CustomToolTip from "../../../../../../../CommonComponents/CustomToolTip";
import { useUpdateSelectedNodes, isFieldValid, FIELD_VALIDATION, showInvalidFieldToasts } from "../../hooks/ThermalDesignerHooks";

import { MinimizeIcon, CloseModalIcon, MaximizeIcon, AdjustSettingsIcon, InfoIcon, DownloadSvg, UploadSvg } from "../../../../SatelliteProfileImgAssets/SatelliteProfileImgAssets";

interface ModalHeaderProps {
  title: string;
  isModalMinimized: boolean;
  setIsModalMinimized: (v: boolean) => void;
  setOpenModal?: React.Dispatch<React.SetStateAction<any>>;
  modalItem?: string | any;
  onClose?: () => void;
  /** Called when the Export JSON button is clicked. Provide to show the button. */
  onExportJson?: () => void;
  /** Called when the Export CSV button is clicked. Provide to show a second export button. */
  onExportCsv?: () => void;
  /** Called with the selected File when the Import button is used. Provide to show the button. */
  onImport?: (file: File) => void;
}

export const ThermalModalHeader = ({ header }: { header: ModalHeaderProps }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) header.onImport?.(file);
    e.currentTarget.value = "";
  };

  return (
    <div className="nodeSpecsModal__header">
      <div className="nodeSpecsModal__header__title">{header.title}</div>
      <div className="nodeSpecsModal__header__buttons">
        {header.onExportJson && (
          <CustomToolTip title={header.onExportCsv ? "Export JSON" : "Export"} placement="bottom">
            <button className="nodeSpecsModal__io-btn" type="button" onClick={header.onExportJson}>
              <DownloadSvg />
            </button>
          </CustomToolTip>
        )}
        {header.onExportCsv && (
          <CustomToolTip title="Export CSV" placement="bottom">
            <button className="nodeSpecsModal__io-btn nodeSpecsModal__io-btn--csv" type="button" onClick={header.onExportCsv}>
              <DownloadSvg /><span>CSV</span>
            </button>
          </CustomToolTip>
        )}
        {header.onImport && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              hidden
              accept=".json,.csv"
              onChange={handleFileChange}
            />
            <CustomToolTip title="Import" placement="bottom">
              <button className="nodeSpecsModal__io-btn" type="button" onClick={() => fileInputRef.current?.click()}>
                <UploadSvg />
              </button>
            </CustomToolTip>
          </>
        )}
        <button onClick={() => header.setIsModalMinimized(!header.isModalMinimized)}>{header.isModalMinimized ? <MaximizeIcon /> : <MinimizeIcon />}</button>
        <button
          onClick={() =>
            header.onClose
              ? header.onClose()
              : header.setOpenModal?.((prev) => ({
                ...prev,
                [header.modalItem]: false,
              }))
          }
        >
          <CloseModalIcon />
        </button>
      </div>
    </div>
  );
};

export const ThermalModalFooter = ({
  onSave = () => { },
  onSaveAndNext = () => { },
  onCancel = () => { },
  isLastNode = false,
  saveLabel,
}: {
  onSave?: () => void;
  onSaveAndNext?: () => void;
  onCancel?: () => void;
  isLastNode?: boolean;
  saveLabel?: string;
} = {}) => {
  const buttonLabel = saveLabel ?? (isLastNode ? "Save & Exit" : "Save & Next");
  const buttonHandler = isLastNode ? onSave : onSaveAndNext;

  return (
    <div className="nodeSpecsModal__footer">
      <button className="nodeSpecsModal__cancelBtn" onClick={onCancel}>
        Cancel
      </button>
      <button className="nodeSpecsModal__saveBtn" onClick={buttonHandler}>
        {buttonLabel}
      </button>
    </div>
  );
};

export const ThermalSingleInputItem = ({ label, value, unit, onChange, fieldName, invalidFields, setInvalidFields, convertToNumber, required = false }) => {
  const [localValue, setLocalValue] = useState(
    value !== undefined && value !== null && !Number.isNaN(value) ? value.toString() : ""
  );
  const [isInvalid, setIsInvalid] = useState(false);

  useEffect(() => {
    setLocalValue(value !== undefined && value !== null && !Number.isNaN(value) ? value.toString() : "");
  }, [value]);

  // Sync with parent invalidFields state
  useEffect(() => {
    if (fieldName && invalidFields?.has?.(fieldName)) {
      setIsInvalid(true);
    } else {
      setIsInvalid(false);
    }
  }, [invalidFields, fieldName]);

  const handleChange = (e) => {
    const filtered = e.target.value.replace(/[^-0-9.]/g, "");
    const dotIndex = filtered.indexOf(".");
    const clamped = dotIndex >= 0 ? filtered.slice(0, dotIndex + 6) : filtered;
    setLocalValue(clamped);

    // Clear this field from invalidFields when user starts editing
    if (fieldName && invalidFields?.has?.(fieldName)) {
      setInvalidFields?.((prev) => {
        const newSet = new Set(prev);
        newSet.delete(fieldName);
        return newSet;
      });
    }
  };

  const handleFocus = (e) => {
    e.target.select();
  };

  const handleBlur = () => {
    const raw = localValue.trim();

    // Empty or bare "-" — field was cleared; commit NaN so validateSelectedNode catches it
    if (raw === "" || raw === "-") {
      onChange?.(NaN);
      if (fieldName && setInvalidFields) {
        setInvalidFields((prev) => {
          const newSet = new Set(prev);
          newSet.add(fieldName);
          showInvalidFieldToasts(new Set([fieldName]));
          return newSet;
        });
      }
      return;
    }

    const numVal = convertToNumber ? convertToNumber(raw) : Number(raw);
    onChange?.(numVal);

    if (fieldName && setInvalidFields) {
      const valid = isFieldValid(fieldName, numVal);
      setInvalidFields((prev) => {
        const newSet = new Set(prev);
        if (valid) {
          newSet.delete(fieldName);
        } else {
          newSet.add(fieldName);
          showInvalidFieldToasts(new Set([fieldName]));
        }
        return newSet;
      });
    }
  };

  const getPlaceholder = () => {
    if (fieldName && fieldName in FIELD_VALIDATION) {
      const config = FIELD_VALIDATION[fieldName];
      return `[${config.min}-${config.max}]`;
    }
    return "0";
  };

  return (
    <div className="infocard-component-properties-div">
      <div className="infocard-component-properties-sub-div">
        <span className="infocard-units-location-span">
          {label}
          {required && <span className="required-asterisk"> *</span>}
        </span>
        <div className="infocard-units-location-sub-div">
          <div className="infocard-component-togglebtn-div">
            <div className="infocard-component-value-div mass-value">
              <input
                type="text"
                className={`infocard-location-value ${isInvalid ? "infocard-location-value--invalid" : ""} input-editable`}
                value={localValue}
                placeholder={getPlaceholder()}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
            </div>
            <div className="infocard-component-units-div">
              <span className="infocard-location-y-axis">{unit}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ThermalSingleInputItemWrapper = ({ label, value, unit, fieldName, invalidFields, setInvalidFields, convertToNumber, required = false }) => {
  const { updateSelectedNodes } = useUpdateSelectedNodes();

  return (
    <ThermalSingleInputItem
      label={label}
      unit={unit}
      value={value}
      onChange={(newValue) => updateSelectedNodes(fieldName, newValue)}
      fieldName={fieldName}
      invalidFields={invalidFields}
      setInvalidFields={setInvalidFields}
      convertToNumber={convertToNumber}
      required={required}
    />
  );
};

const convertValuesToStrings = (vals: any[] | undefined) => vals?.map((v) => (v !== undefined && v !== null ? v.toString() : "")) ?? ["", "", ""];

export const ThermalThreeInputItems = ({ values, labels, labelOrientation, selectedNode, invalidFields, setInvalidFields, convertToNumber }: { values: any; labels: any; labelOrientation: any; selectedNode: any; invalidFields?: Set<string>; setInvalidFields?: React.Dispatch<React.SetStateAction<Set<string>>>; convertToNumber?: (v: any) => number }) => {
  const { updateSelectedNodes } = useUpdateSelectedNodes();

  const [localValues, setLocalValues] = useState(convertValuesToStrings(values));
  const [invalidIndices, setInvalidIndices] = useState<Set<number>>(new Set());

  useEffect(() => {
    setLocalValues(convertValuesToStrings(values));
  }, [values]);

  // Sync with parent invalidFields state
  useEffect(() => {
    if (invalidFields?.has?.("node_body_vec")) {
      // If node_body_vec is invalid, mark individual elements as invalid
      const newInvalid = new Set<number>();
      if (values && Array.isArray(values)) {
        values.forEach((val, index) => {
          if (!isFieldValid("node_body_vec", val)) {
            newInvalid.add(index);
          }
        });
      }
      setInvalidIndices(newInvalid);
    } else {
      // Clear invalid indices when field is no longer invalid
      setInvalidIndices(new Set());
    }
  }, [invalidFields, values]);

  const handleInputChange = (index: number, val: string) => {
    const filtered = val.replace(/[^-0-9.]/g, "");
    const dotIndex = filtered.indexOf(".");
    const clamped = dotIndex >= 0 ? filtered.slice(0, dotIndex + 6) : filtered;
    const newLocal = [...localValues];
    newLocal[index] = clamped;
    setLocalValues(newLocal);

    // Clear node_body_vec from invalidFields when user starts editing
    if (invalidFields?.has?.("node_body_vec")) {
      setInvalidFields?.((prev) => {
        const newSet = new Set(prev);
        newSet.delete("node_body_vec");
        return newSet;
      });
    }
  };

  const handleInputFocus = (e) => {
    // Select all text on focus for easy replacement
    e.currentTarget.select();
  };

  const handleInputBlur = (index: number) => {
    if (selectedNode) {
      const numVal = convertToNumber ? convertToNumber(localValues[index]) : Number(localValues[index]);
      const newBodyVec = [...(selectedNode.values?.node_body_vec ?? [0, 0, 0])];
      newBodyVec[index] = numVal;
      updateSelectedNodes("node_body_vec", newBodyVec);

      if (setInvalidFields) {
        const valid = isFieldValid("node_body_vec", newBodyVec);
        setInvalidFields((prev) => {
          const newSet = new Set(prev);
          if (valid) {
            newSet.delete("node_body_vec");
          } else {
            newSet.add("node_body_vec");
            showInvalidFieldToasts(new Set(["node_body_vec"]));
          }
          return newSet;
        });
      }
    }
  };

  const getAxisLabel = (index: number): string => {
    const axisNames = ["x", "y", "z"];
    return labelOrientation === 2 ? "y" : axisNames[index];
  };

  const getPlaceholder = (): string => {
    const config = FIELD_VALIDATION["node_body_vec"];
    return `[${config.min}-${config.max}]`;
  };

  return (
    <div className="infocard-three-inputs-div">
      {localValues.map((val, index) => (
        <div key={index} className={labelOrientation ? "infocard-location-div orientation" : "infocard-location-div infocard-location-div-reverse"}>
          <div className="infocard-location-value-div">
            <input
              type="text"
              className={`infocard-location-value-orientation text-truncate ${invalidIndices.has(index) ? "infocard-location-value-orientation--invalid" : ""} input-editable`}
              value={val}
              placeholder={getPlaceholder()}
              onChange={(e) => handleInputChange(index, e.target.value)}
              onFocus={(e) => handleInputFocus(e)}
              onBlur={() => handleInputBlur(index)}
            />
          </div>
          <div className="infocard-location-x-axis-div">
            <span className={`infocard-location-${getAxisLabel(index)}-axis`}>{labels[index]}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

const MATERIAL_PROPERTY_LABELS: { key: keyof MaterialsData; label: string; unit?: string }[] = [
  { key: "specific_heat", label: "Specific Heat", unit: "J/(kg·K)" },
  { key: "thermal_conductivity", label: "Thermal Conductivity", unit: "W/(m·K)" },
  { key: "density", label: "Density", unit: "kg/m³" },
  { key: "absorptivity", label: "Absorptivity" },
  { key: "emissivity", label: "Emissivity" },
  { key: "youngs_modulus", label: "Young's Modulus", unit: "GPa" },
  { key: "yield_strength", label: "Yield Strength", unit: "MPa" },
  { key: "cte", label: "CTE", unit: "ppm/\u00B0C" },
];

export const ThermalMaterialInfoPopUp = ({ materialInfo }: { materialInfo?: MaterialsData }) => {
  if (!materialInfo) return null;
  return (
    <div className="material_info_popup">
      <span className="material_info_popup__name">{materialInfo.material_name}</span>
      {MATERIAL_PROPERTY_LABELS.map(({ key, label, unit }) => {
        const val = materialInfo[key as keyof MaterialsData];
        if (val === undefined || val === null) return null;
        return (
          <div key={key} className="material_info_popup__row">
            <span className="material_info_popup__label">{label}</span>
            <span className="material_info_popup__value">
              {String(val)}
              {unit && <> {unit}</>}
            </span>
          </div>
        );
      })}
    </div>
  );
};

/**
 * Manages open/close state and fixed position for a portal popup anchored to a trigger element.
 * Returns refs for the trigger and popup, a toggle handler, and a render helper.
 */
const useAnchoredPopup = (offsetX = 20) => {
  const [isOpen, setIsOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handle = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || popupRef.current?.contains(t)) return;
      setIsOpen(false);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [isOpen]);

  const toggle = () => {
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({ top: rect.top + rect.height / 2, left: rect.right + offsetX });
    }
    setIsOpen((v) => !v);
  };

  const renderPopup = (children: React.ReactNode) =>
    isOpen && pos && typeof document !== "undefined"
      ? createPortal(
          <div ref={popupRef} style={{ position: "fixed", top: pos.top, left: pos.left, transform: "translateY(-50%)", zIndex: 9999 }}>
            {children}
          </div>,
          document.body
        )
      : null;

  return { isOpen, triggerRef, toggle, renderPopup };
};

export const ThermalMaterialDropdownInputItem = ({
  label,
  materialOptions,
  selectedMaterial,
  onSelectMaterial,
  onEditCustomMaterial,
  materialProperties,
  required = false,
  invalidFields,
  setInvalidFields,
}: {
  label: string;
  materialOptions: any[];
  selectedMaterial: string | null;
  onSelectMaterial?: (v: string) => void;
  onEditCustomMaterial?: () => void;
  materialProperties?: MaterialsData;
  required?: boolean;
  invalidFields?: Set<string>;
  setInvalidFields?: React.Dispatch<React.SetStateAction<Set<string>>>;
}) => {
  const { isOpen: showInfo, triggerRef, toggle: handleToggleInfo, renderPopup } = useAnchoredPopup();

  return (
    <div className="infocard-component-properties-div">
      <div className="infocard-component-properties-sub-div">
        <span className="infocard-units-location-span">
          {label}
          {required && <span className="required-asterisk"> *</span>}
        </span>
        <div className={`infocard-units-location-sub-div infocard-units-location-sub-div--material${invalidFields?.has("node_material") ? " infocard-units-location-sub-div--material--invalid" : ""}`}>
          <ModalDropdown
            options={materialOptions}
            isMulti={false}
            handleSelectedOption={(e) => {
              const val = e?.value ?? "";
              setInvalidFields?.((prev) => {
                const newSet = new Set(prev);
                if (val) {
                  newSet.delete("node_material");
                } else {
                  newSet.add("node_material");
                  showInvalidFieldToasts(new Set(["node_material"]));
                }
                return newSet;
              });
              onSelectMaterial?.(val);
            }}
            previousOption={selectedMaterial}
            placeholder="Select material"
            menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
            menuPosition="fixed"
          />
          {selectedMaterial === "Custom" && (
            <CustomToolTip title="Edit Custom material properties" placement="top">
              <button className="custom-material-button" onClick={onEditCustomMaterial}>
                <AdjustSettingsIcon />
              </button>
            </CustomToolTip>
          )}
          {selectedMaterial && selectedMaterial !== "" && (
            <div className="material_info_trigger" ref={triggerRef}>
              <CustomToolTip title="View material properties" placement="top">
                <button className="material_info_btn" onClick={handleToggleInfo}>
                  <InfoIcon isActive={showInfo} />
                </button>
              </CustomToolTip>
            </div>
          )}
        </div>
      </div>
      {renderPopup(<ThermalMaterialInfoPopUp materialInfo={materialProperties} />)}
    </div>
  );
};
